import { AugmentRarity } from "@ugg/shared/hooks/aram-mayhem";
import { AUGMENT_RARITY_ALL } from "@ugg/shared/query-params/filter-options/aram-mayhem";

// ─────────────────────────────────────────────────────────────────────
// Rarity ordering, labels, and per-rarity styling for the ARAM Mayhem
// augments module on the champion build page.
//
// Same split as common/tier-card-styles.ts: the wire enum lives in the
// data layer (hooks/aram-mayhem.ts), everything about how a rarity is
// ordered, named, and painted lives here next to its only consumer.
//
// RARITY_ORDER / RARITY_LABEL / resolveRarityParam were deliberately
// duplicated here rather than imported from the global augment tier list's
// equivalents, so that page could be retired without taking this one with it.
// It since was, and those copies went with it — this module is now the only
// definition, with nothing left to keep in sync.
// ─────────────────────────────────────────────────────────────────────

// Best-to-worst, matching the canonical RARITY_DISPLAY_ORDER on the Python
// side. Explicit (rather than Object.keys over the fetched data) so React's
// section order can't drift if upstream JSON key ordering changes.
export const RARITY_ORDER = ["kPrismatic", "kGold", "kSilver"] as const satisfies readonly AugmentRarity[];

// Human-readable labels, decoupled from the JSON's `k*` enum keys so display
// tweaks live here instead of at every render site.
export const RARITY_LABEL: Record<AugmentRarity, string> = {
  kPrismatic: "Prismatic",
  kGold: "Gold",
  kSilver: "Silver",
};

// URL value → wire key. The query param is the friendly form (`?rarity=gold`),
// the data is keyed `kGold`; see query-params/filter-options/aram-mayhem.ts.
const PARAM_TO_RARITY: Record<string, AugmentRarity> = {
  prismatic: "kPrismatic",
  gold: "kGold",
  silver: "kSilver",
};

// Resolve a `rarity` query value to a wire key, or undefined for "all" / an
// unrecognised value (validateQueryParams already drops values outside the
// option list, so undefined here means "don't filter").
export function resolveRarityParam(value: unknown): AugmentRarity | undefined {
  return typeof value === "string" && value !== AUGMENT_RARITY_ALL ? PARAM_TO_RARITY[value] : undefined;
}

// IMPORTANT: complete literal class strings, never interpolated, so
// Tailwind's content scanner emits the rules at build time. Both the website
// and monoapp configs glob `@ugg/shared/**/*.{ts,tsx}`, so this path is
// covered — but don't rewrite any of these into template strings. Same
// requirement (and same reason) as common/tier-card-styles.ts.
//
// The three colors are pulled from values already in the codebase rather than
// invented: #CA76FF is the violet end of the Arena prismatic-item gradient,
// #D3A752 is accent-gold-500, #A9ACD1 is accent-gray-300.

// Augment cell: a tinted wash behind a stronger border of the same hue.
export const AUGMENT_RARITY_CELL_CLASSES: Record<AugmentRarity, string> = {
  kPrismatic: "border-[#CA76FF]/50 bg-[#CA76FF]/[0.07]",
  kGold: "border-[#D3A752]/50 bg-[#D3A752]/[0.07]",
  kSilver: "border-[#A9ACD1]/50 bg-[#A9ACD1]/[0.07]",
};

// Section heading bullet. Overrides HeaderBullet's default blue `::before`,
// so the heading picks up the rarity color the cells below it use.
export const AUGMENT_RARITY_BULLET_CLASSES: Record<AugmentRarity, string> = {
  kPrismatic: "before:bg-[#CA76FF]",
  kGold: "before:bg-[#D3A752]",
  kSilver: "before:bg-[#A9ACD1]",
};
